/**
 * Turn a FastAPI error response into a message worth showing a submitter.
 *
 * The broker replies in three shapes. `ServiceError` is rendered by
 * barti_fastapi's handler as `{"message": ...}`; `HTTPException` (the auth
 * failures in `api/deps.py`) returns a string `detail`; request validation
 * returns an array of per-field errors under `detail`. Anything unrecognised (an
 * HTML error page from a proxy, a truncated body) falls back to a generic message
 * carrying the status.
 *
 * A util rather than a service function — it performs no request, so it stays
 * camelCase per the monorepo naming convention.
 */

/** A validation problem reported by FastAPI's 422 handler. */
interface ValidationErrorItem {
  loc?: (string | number)[]
  msg?: string
}

interface ErrorBody {
  message?: string
  detail?: string | ValidationErrorItem[]
}

export async function describeApiError(response: Response): Promise<string> {
  let body: ErrorBody
  try {
    body = (await response.json()) as ErrorBody
  } catch {
    return `The broker rejected the request (HTTP ${response.status}).`
  }

  // First: every ServiceError the broker raises lands here, including the two a
  // client can actually reach — oversized metadata (400) and a signing failure
  // (503). Reading only `detail` would swallow both into the generic fallback.
  if (typeof body.message === 'string' && body.message) {
    return body.message
  }

  const detail = body.detail
  if (typeof detail === 'string') {
    return detail
  }
  if (Array.isArray(detail)) {
    const messages = detail
      .map((item) => {
        const field = item.loc?.at(-1)
        return field !== undefined && item.msg !== undefined
          ? `${String(field)}: ${item.msg}`
          : item.msg
      })
      .filter((message): message is string => Boolean(message))
    if (messages.length > 0) {
      return messages.join('; ')
    }
  }
  return `The broker rejected the request (HTTP ${response.status}).`
}
